import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetAttendance,
  SaveAttendance,
  QuickClockAttendance,
  GetMonthlyAttendanceSheet,
  GetEmployeeMonthlyLogs,
} from "../Api";
import { DEFAULT_STALE_TIME, ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";

export const attendanceQueryKeys = {
  attendance: (params: Parameters<typeof GetAttendance>[0]) =>
    ["attendance", params] as const,
  monthlySheet: (params: Parameters<typeof GetMonthlyAttendanceSheet>[0]) =>
    ["monthlySheet", params] as const,
  employeeLogs: (params: Parameters<typeof GetEmployeeMonthlyLogs>[0]) =>
    ["employeeLogs", params] as const,
};

export const useAttendanceQuery = (
  params: Parameters<typeof GetAttendance>[0],
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: attendanceQueryKeys.attendance(params),
    queryFn: async () => await GetAttendance(params),
  });
};

export const useSaveAttendanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof SaveAttendance>[0]) =>
      SaveAttendance(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["attendance"] });
        queryClient.invalidateQueries({ queryKey: ["monthlySheet"] });
      }
    },
  });
};

export const useQuickClockMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof QuickClockAttendance>[0]) =>
      QuickClockAttendance(payload),
    onSuccess: (res) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: ["attendance"] });
        queryClient.invalidateQueries({ queryKey: ["monthlySheet"] });
      }
    },
  });
};

export const useMonthlyAttendanceSheetQuery = (
  params: Parameters<typeof GetMonthlyAttendanceSheet>[0],
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: attendanceQueryKeys.monthlySheet(params),
    queryFn: async () => unwrapApiResponse(await GetMonthlyAttendanceSheet(params)),
  });
};

export const useEmployeeMonthlyLogsQuery = (
  params: Parameters<typeof GetEmployeeMonthlyLogs>[0],
  options?: ServiceQueryOptions
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    enabled: !!params.employee_id && !!params.month,
    ...options,
    queryKey: attendanceQueryKeys.employeeLogs(params),
    queryFn: async () => unwrapApiResponse(await GetEmployeeMonthlyLogs(params)),
  });
};
